import { KeyboardEvent, useCallback } from 'react';
import { RedirectRequest } from '@azure/msal-browser';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { authenticate, buildB2cPolicies, getWebStoreApiConfig } from '@gn-audio/core';
import {
  Button,
  isColorBright,
  resolveColorProperty,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import { useSiteSettings } from '@gn-audio/jss-components/utils/contexts/SiteSettingsContext';
import { AuthorizationSettings, getWebStoreApiClient } from '@gn-audio/jss-core';

import { Eyebrow } from 'common/components/Eyebrow';
import { CardTypes } from 'composables/LoginSpot/CardTypesConstants';
import { Card } from 'composables/LoginSpot/LoginSpotTypes';

type LoginCardProps = {
  card: Card;
};

const LoginCard = ({ card }: LoginCardProps) => {
  const { t } = useFallbackTranslation();
  const { authorization } = useSiteSettings();

  const isBackgroundColorGradient = (backgroundColor: string | undefined) => {
    return backgroundColor?.startsWith('linear-gradient');
  };
  const backgroundColor = card.backgroundColor?.value;
  const cardBackgroundColor = isBackgroundColorGradient(backgroundColor)
    ? backgroundColor
    : (resolveColorProperty(backgroundColor) ?? 'transparent');
  const isBgBright = isColorBright(cardBackgroundColor as '#{string}');

  const signIn = useCallback(async () => {
    try {
      const webStoreApiClient = getWebStoreApiClient();
      const account = webStoreApiClient.account;

      if (!account) {
        await webStoreApiClient.signIn();
      }
    } catch (error) {
      console.error(`Error during sign in:`, error);
    }
  }, []);

  const signUp = useCallback(async () => {
    try {
      const { marketLocale, scopes } = getWebStoreApiConfig();
      const { authorities } = buildB2cPolicies(authorization as AuthorizationSettings);
      const signUpRequest: RedirectRequest = {
        authority: authorities.signUp.authority,
        extraQueryParameters: { ui_locales: marketLocale },
        scopes,
      };
      await authenticate(signUpRequest as never);
    } catch (error) {
      console.error('Error during sign up:', error);
    }
  }, [authorization]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      card.cardType?.value === CardTypes.logIn ? signIn() : signUp();
    }
  };

  if (!card.cardType?.value) {
    return null;
  }

  const isLoginCard = card.cardType?.value === CardTypes.logIn;
  const hoverTextWhiteClass = isLoginCard ? 'group-hover:text-white' : '';
  const baseTransitionClass = 'transition-colors duration-300';

  return (
    <div
      onClick={card.cardType?.value === CardTypes.logIn ? signIn : signUp}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      className={classNames(
        'group relative min-h-[460px] hover:cursor-pointer hover:no-underline',
        'flex items-end justify-between overflow-hidden rounded-lg px-6 py-2 pb-6',
        'focus-visible:rounded-lg focus-visible:outline-none focus-visible:outline-focus',
        card.cardType?.value === CardTypes.logIn && 'text-white lg:col-span-2'
      )}
      {...(card.backgroundColor?.value && {
        style: {
          background: cardBackgroundColor,
          color: isBgBright ? 'black' : 'white',
        },
      })}
    >
      {!!card.backgroundImage?.value?.src && (
        <>
          <div className="absolute inset-0 z-10 rounded-3xl bg-gradient-to-t from-[rgba(0,0,0,0.5)] via-[rgba(0,0,0,0.5)] to-60%" />
          <Image
            field={card.backgroundImage}
            className="absolute bottom-0 left-0 right-0 top-0 h-full min-h-full w-full min-w-full object-cover"
            alt={card.backgroundImage?.value?.alt}
            loading="lazy"
          />
        </>
      )}
      <div className="relative z-20 w-full">
        {card.cardType?.value === CardTypes.logIn && (
          <div className="absolute -bottom-[1.5rem] -left-[1.5rem] h-[calc(100%+3rem)] w-[calc(100%+3rem)] -translate-x-full rounded-r-full bg-blue-800 opacity-90 transition-transform duration-300 group-hover:translate-x-0" />
        )}
        <div className="relative flex flex-col items-start">
          <Eyebrow
            text={card.eyebrowText?.value}
            className={classNames('mb-4', baseTransitionClass, hoverTextWhiteClass)}
          />
          <p className={classNames('heading-2', baseTransitionClass, hoverTextWhiteClass)}>
            {card.heading?.value}
          </p>
          <p className={classNames('paragraph mb-6', baseTransitionClass, hoverTextWhiteClass)}>
            {card.description?.value}
          </p>

          {card.cardType?.value && (
            <Button
              variant={card.cardType?.value === CardTypes.logIn ? 'primary' : 'secondary'}
              size="m"
              noKeyboard
            >
              <span
                className={
                  !card.backgroundImage?.value?.src &&
                  !isBgBright &&
                  card.cardType?.value !== CardTypes.logIn
                    ? 'text-white'
                    : undefined
                }
              >
                {card.cardType?.value === CardTypes.logIn ? t('system.SignIn') : t('system.Signup')}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginCard;
